import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess } from '../lib/api';
import { BrandLogo } from '../components/BrandLogo';
import { RamhCredit } from '../components/RamhCredit';
import { SAUDI_CITIES, getDistrictsForCity } from '../lib/saudi-locations';
import { SEO } from '../components/SEO';
import { LocationPicker } from '../components/LocationPicker';

type Step = 'intro' | 'location' | 'land' | 'building' | 'family' | 'preferences' | 'generate' | 'result';

interface Occupant { label: string; ageGroup?: 'adult' | 'kid' }

export function EngineeringDesignPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('intro');
  const [projectId, setProjectId] = useState<string | null>(null);

  // 📊 الفورم
  const [form, setForm] = useState({
    city: '', district: '', address: '', latitude: null as number | null, longitude: null as number | null,
    landShape: 'RECTANGULAR' as 'RECTANGULAR' | 'L_SHAPED' | 'IRREGULAR' | 'CORNER',
    frontageWidth: '', depth: '', totalArea: '',
    streets: [{ side: 'south' as 'north' | 'south' | 'east' | 'west', width: 15, name: '' }],
    buildingType: '' as '' | 'VILLA' | 'PERSONAL_HOUSE' | 'CHALET' | 'REST_HOUSE' | 'APARTMENT_BLD' | 'WAREHOUSE' | 'OFFICES' | 'COMMERCIAL',
    floorsCount: 1,
    hasBasement: false, hasRoofRoom: false,
    familyMembers: 4,
    occupants: [] as Occupant[],
    preferences: {
      hasMajlis: true, hasWomenMajlis: false, hasMaidRoom: false,
      hasGarden: true, hasPool: false, parkingSpots: 2,
      style: 'modern' as 'modern' | 'classic' | 'andalusian',
    },
  });

  // 🚧 معطّل مؤقتاً — قيد التطوير
  const createProj = useMutation({
    mutationFn: async () => {
      throw new Error('UNDER_DEVELOPMENT');
    },
    onError: () => setStep('generate'),
  });

  const generateMut = useMutation({
    mutationFn: async (_id: string) => {
      throw new Error('UNDER_DEVELOPMENT');
    },
  });

  const isResidential = ['VILLA', 'PERSONAL_HOUSE', 'CHALET', 'REST_HOUSE'].includes(form.buildingType);
  const isCommercial = ['WAREHOUSE', 'OFFICES', 'COMMERCIAL'].includes(form.buildingType);
  const totalCalculated = Number(form.frontageWidth) * Number(form.depth) || 0;

  const goNext = () => {
    if (step === 'intro') setStep('location');
    else if (step === 'location') setStep('land');
    else if (step === 'land') setStep('building');
    else if (step === 'building') {
      if (isResidential) setStep('family');
      else setStep('preferences');
    }
    else if (step === 'family') setStep('preferences');
    else if (step === 'preferences') createProj.mutate();
  };
  const goBack = () => {
    if (step === 'location') setStep('intro');
    else if (step === 'land') setStep('location');
    else if (step === 'building') setStep('land');
    else if (step === 'family') setStep('building');
    else if (step === 'preferences') setStep(isResidential ? 'family' : 'building');
  };

  return (
    <div className="theme-engineering max-w-7xl mx-auto px-4 py-8">
      <SEO
        title="التصميم الهندسي والمعماري 🏗️"
        description="خدمة تصميم معماري ذكي للفلل والمنازل في السعودية. مخططات جاهزة، تصاميم مودرن وكلاسيكية ونجدية، استشارات هندسية معتمدة من مهندسين سعوديين."
        keywords="تصميم معماري, تصميم هندسي, تصاميم فلل, تصاميم منازل, مخططات منازل, مخططات فلل, مخططات معمارية, مكتب هندسي, مهندس معماري, تصميم فيلا, تصميم منزل, تصاميم مودرن, تصاميم كلاسيكية, تصاميم نجدية, مكتب تصميم الرياض, مهندس تصميم سعودي, شركة صورة العقارية, img.sa"
        canonical="https://img.sa/engineering"
      />
      {/* 🏷️ عنوان الصفحة (نفس نمط بقية الصفحات) */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-3xl font-black text-navy-dark mb-2 flex items-center gap-2">
            🏗️ منصة التصميم الهندسي الذكية
          </h1>
          <p className="text-ink-secondary">
            أدخل معطيات أرضك واحتياجك → نولّد لك تخطيطاً احترافياً مع فحص امتثال البلدية وتقدير التكلفة.
          </p>
        </div>
        <Badge color="amber">🚧 قيد التطوير</Badge>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* 🚧 إشعار قيد التطوير */}
        <Card className="mb-6 border-2 border-amber-300 bg-gradient-to-l from-amber-50 to-yellow-50">
          <CardBody>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="text-5xl">🚧</div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-black text-amber-900 mb-1">المنصة قيد التطوير</h3>
                <p className="text-sm text-amber-800 leading-relaxed">
                  نعمل حالياً على إكمال بقية المنصة — هذا القسم سيعود مع تحسينات معمارية كبيرة.
                  يمكنك تصفّح الواجهة، لكن توليد التصميم الفعلي مؤقتاً معطّل.
                </p>
              </div>
              <Link to="/"><Button variant="ghost" size="sm">← العودة للرئيسية</Button></Link>
            </div>
          </CardBody>
        </Card>

        {/* Stepper */}
        {step !== 'intro' && step !== 'result' && (
          <div className="mb-6">
            <Stepper current={step} isResidential={isResidential} />
          </div>
        )}

        {/* محتوى الخطوة */}
        <Card>
          <CardBody>
            {step === 'intro'       && <IntroStep onStart={() => setStep('location')} />}
            {step === 'location'    && <LocationStep form={form} setForm={setForm} onBack={goBack} onNext={goNext} />}
            {step === 'land'        && <LandStep form={form} setForm={setForm} totalCalculated={totalCalculated} onBack={goBack} onNext={goNext} />}
            {step === 'building'    && <BuildingStep form={form} setForm={setForm} onBack={goBack} onNext={goNext} />}
            {step === 'family'      && <FamilyStep form={form} setForm={setForm} onBack={goBack} onNext={goNext} />}
            {step === 'preferences' && <PreferencesStep form={form} setForm={setForm} isCommercial={isCommercial} onBack={goBack} onNext={goNext} loading={createProj.isPending} />}
            {step === 'generate'    && <GeneratingStep error={generateMut.error?.message ?? createProj.error?.message} onRetry={() => projectId ? generateMut.mutate(projectId) : createProj.mutate()} />}
            {step === 'result'      && projectId && <ResultStep projectId={projectId} result={generateMut.data?.data} onBack={() => setStep('intro')} navigate={navigate} />}
          </CardBody>
        </Card>

        <div className="mt-6 text-center"><RamhCredit /></div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Components ━━━━━━━━━━━━━━━━━━━━

function Stepper({ current, isResidential }: { current: Step; isResidential: boolean }) {
  const steps = isResidential
    ? [['location', '📍 الموقع'], ['land', '📐 الأرض'], ['building', '🏢 المبنى'], ['family', '👨‍👩‍👧 الأسرة'], ['preferences', '✨ التفضيلات']]
    : [['location', '📍 الموقع'], ['land', '📐 الأرض'], ['building', '🏢 المبنى'], ['preferences', '✨ التفضيلات']];
  const currIdx = steps.findIndex(([k]) => k === current);
  return (
    <div className="flex items-center justify-between gap-1 overflow-x-auto">
      {steps.map(([key, label], i) => (
        <div key={key} className="flex items-center gap-1 flex-shrink-0">
          <div className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${
            i < currIdx ? 'bg-green-500 text-white' :
            i === currIdx ? 'bg-navy-dark text-white' : 'bg-gray-200 text-gray-500'
          }`}>{i < currIdx ? '✓' : i + 1} {label}</div>
          {i < steps.length - 1 && <div className={`w-4 h-0.5 ${i < currIdx ? 'bg-green-500' : 'bg-gray-300'}`} />}
        </div>
      ))}
    </div>
  );
}

function IntroStep({ onStart: _onStart }: { onStart: () => void }) {
  return (
    <div className="text-center py-8">
      <div className="text-6xl mb-4">🏗️</div>
      <h2 className="text-3xl font-black text-navy-dark mb-3">منصة التصميم الهندسي الذكية</h2>
      <p className="text-gray-600 max-w-2xl mx-auto mb-6">
        أدخل معطيات أرضك واحتياجاتك، وسنقوم بتوليد تصميم تخطيطي ذكي خلال ثوانٍ — مع فحص امتثال البلدية،
        تقدير التكلفة، واقتراحات هندسية مخصّصة لك.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl mx-auto mb-8 text-right">
        <Feature icon="🤖" title="توليد ذكي" desc="غرف حسب أسرتك ونوع البناء" />
        <Feature icon="🏛️" title="امتثال بلدي" desc="فحص قواعد الحي والارتفاعات" />
        <Feature icon="💰" title="تقدير التكلفة" desc="حساب فوري لمواد البناء" />
      </div>

      {/* 🚧 زر معطّل + شارة "تحت التطوير" */}
      <div className="inline-flex flex-col items-center gap-3">
        <button
          disabled
          className="!px-10 !py-3 !text-base bg-gray-300 text-gray-500 rounded-xl font-bold cursor-not-allowed inline-flex items-center gap-2"
          title="هذه الميزة قيد التطوير حالياً"
        >
          🚧 ابدأ التصميم — قيد التطوير
        </button>
        <p className="text-xs text-amber-700 max-w-md">
          نعمل على إكمال المحرك الهندسي. ستُتاح الميزة قريباً مع تحسينات معمارية كبيرة.
        </p>
        <Link to="/" className="mt-2"><Button variant="ghost" size="sm">← العودة للرئيسية</Button></Link>
      </div>
    </div>
  );
}

function Feature({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-3xl mb-2">{icon}</div>
      <div className="font-black text-navy-dark mb-1">{title}</div>
      <div className="text-xs text-gray-500">{desc}</div>
    </div>
  );
}

function LocationStep({ form, setForm, onBack, onNext }: { form: never; setForm: never; onBack: () => void; onNext: () => void }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const f = form as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sf = setForm as any;
  const districts = [...new Set(getDistrictsForCity(f.city))];  // dedup
  const customDistrict = f.district === '__custom__' || (f.city && f.district && !districts.includes(f.district));

  return (
    <div>
      <h2 className="text-2xl font-black text-navy-dark mb-2">📍 موقع الأرض</h2>
      <p className="text-gray-600 mb-6 text-sm">حدد المدينة والحي — مطلوب لفحص امتثال البلدية وقواعد البناء في الحي.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-bold text-ink mb-1">المدينة *</label>
          <select
            value={f.city}
            onChange={(e) => sf({ ...f, city: e.target.value, district: '' })}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
          >
            <option value="">— اختر المدينة —</option>
            {SAUDI_CITIES.map((c) => (
              <option key={c.name} value={c.name}>{c.name} {c.region !== c.name ? `(${c.region})` : ''}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-bold text-ink mb-1">الحي *</label>
          <select
            value={customDistrict ? '__custom__' : f.district}
            onChange={(e) => sf({ ...f, district: e.target.value === '__custom__' ? '' : e.target.value })}
            disabled={!f.city}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">{f.city ? '— اختر الحي —' : 'اختر المدينة أولاً'}</option>
            {districts.map((d) => <option key={d} value={d}>{d}</option>)}
            <option value="__custom__">+ حي آخر (اكتبه)</option>
          </select>
          {customDistrict && (
            <input
              value={f.district}
              onChange={(e) => sf({ ...f, district: e.target.value })}
              placeholder="اكتب اسم الحي"
              className="w-full px-4 py-2.5 mt-2 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            />
          )}
        </div>

        <div className="md:col-span-2">
          <Input label="العنوان التفصيلي (اختياري)" value={f.address} onChange={(e) => sf({ ...f, address: e.target.value })} placeholder="الشارع، رقم القطعة..." />
        </div>
        <div className="md:col-span-2">
          <LocationPicker
            label="📍 موقع الأرض على الخريطة (اختياري)"
            value={{ lat: f.latitude, lng: f.longitude, address: f.address }}
            onChange={(v) => sf({ ...f, latitude: v.lat, longitude: v.lng, address: v.address ?? f.address })}
            city={f.city}
            height={350}
          />
        </div>
      </div>
      <Nav onBack={onBack} onNext={onNext} canNext={!!(f.city && f.district)} />
    </div>
  );
}

function LandStep({ form, setForm, totalCalculated, onBack, onNext }: { form: never; setForm: never; totalCalculated: number; onBack: () => void; onNext: () => void }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const f = form as any; const sf = setForm as never as (v: never) => void;
  return (
    <div>
      <h2 className="text-2xl font-black text-navy-dark mb-2">📐 أبعاد الأرض</h2>
      <p className="text-gray-600 mb-6 text-sm">أدخل أبعاد أرضك وتفاصيل الشوارع المحيطة.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Input label="عرض الواجهة (متر) *" type="number" value={f.frontageWidth} onChange={(e: never) => sf({ ...f, frontageWidth: (e as never as { target: { value: string } }).target.value } as never)} placeholder="22" />
        <Input label="العمق (متر) *" type="number" value={f.depth} onChange={(e: never) => sf({ ...f, depth: (e as never as { target: { value: string } }).target.value } as never)} placeholder="30" />
        <Input label="المساحة الإجمالية (م²)" type="number" value={f.totalArea || totalCalculated || ''} onChange={(e: never) => sf({ ...f, totalArea: (e as never as { target: { value: string } }).target.value } as never)} hint={`الحساب التلقائي: ${totalCalculated.toFixed(0)}م²`} />
      </div>

      <fieldset className="border border-gray-200 rounded-xl p-4 mb-4">
        <legend className="px-2 text-sm font-bold text-navy-dark">🛣️ الشوارع المحيطة</legend>
        {f.streets.map((s: { side: string; width: number; name?: string }, i: number) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2">
            <select value={s.side} onChange={(e) => { const arr = [...f.streets]; arr[i] = { ...arr[i], side: e.target.value as never }; sf({ ...f, streets: arr } as never); }} className="col-span-3 px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm">
              <option value="north">شمال</option><option value="south">جنوب</option>
              <option value="east">شرق</option><option value="west">غرب</option>
            </select>
            <input type="number" value={s.width} onChange={(e) => { const arr = [...f.streets]; arr[i] = { ...arr[i], width: Number(e.target.value) }; sf({ ...f, streets: arr } as never); }} placeholder="عرض الشارع" className="col-span-3 px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm" />
            <input value={s.name ?? ''} onChange={(e) => { const arr = [...f.streets]; arr[i] = { ...arr[i], name: e.target.value }; sf({ ...f, streets: arr } as never); }} placeholder="اسم الشارع (اختياري)" className="col-span-5 px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm" />
            <button onClick={() => sf({ ...f, streets: f.streets.filter((_: unknown, j: number) => j !== i) } as never)} className="col-span-1 text-red-600 hover:text-red-800">🗑</button>
          </div>
        ))}
        {f.streets.length < 4 && (
          <Button size="sm" variant="ghost" onClick={() => sf({ ...f, streets: [...f.streets, { side: 'north', width: 12, name: '' }] } as never)}>+ شارع آخر</Button>
        )}
      </fieldset>

      <Nav onBack={onBack} onNext={onNext} canNext={!!(f.frontageWidth && f.depth)} />
    </div>
  );
}

function BuildingStep({ form, setForm, onBack, onNext }: { form: never; setForm: never; onBack: () => void; onNext: () => void }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const f = form as any; const sf = setForm as never as (v: never) => void;
  const types = [
    { id: 'VILLA', label: '🏠 فيلا', desc: 'سكن خاص فاخر', residential: true },
    { id: 'PERSONAL_HOUSE', label: '🏡 بيت شخصي', desc: 'سكن خاص متوسط', residential: true },
    { id: 'CHALET', label: '🏖️ شاليه', desc: 'استراحة شاطئية', residential: true },
    { id: 'REST_HOUSE', label: '🌴 استراحة', desc: 'استراحة عائلية', residential: true },
    { id: 'APARTMENT_BLD', label: '🏢 عمارة شقق', desc: 'استثماري سكني', residential: false },
    { id: 'OFFICES', label: '💼 مكاتب إدارية', desc: 'تجاري إداري', residential: false },
    { id: 'COMMERCIAL', label: '🏪 محلات تجارية', desc: 'استثمار تجاري', residential: false },
    { id: 'WAREHOUSE', label: '📦 مستودع', desc: 'تخزين/لوجستي', residential: false },
  ];
  return (
    <div>
      <h2 className="text-2xl font-black text-navy-dark mb-2">🏢 نوع البناء</h2>
      <p className="text-gray-600 mb-6 text-sm">اختر نوع المبنى — السؤال الذكي يتكيّف بناءً على اختيارك.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {types.map((t) => (
          <button key={t.id} onClick={() => sf({ ...f, buildingType: t.id } as never)} className={`p-4 rounded-xl border-2 transition text-right ${f.buildingType === t.id ? 'border-navy bg-navy/5' : 'border-gray-200 hover:border-gray-300'}`}>
            <div className="text-2xl mb-1">{t.label.split(' ')[0]}</div>
            <div className="font-black text-sm">{t.label.split(' ').slice(1).join(' ')}</div>
            <div className="text-xs text-gray-500">{t.desc}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-bold mb-1">عدد الأدوار</label>
          <input type="number" min={1} max={50} value={f.floorsCount} onChange={(e) => sf({ ...f, floorsCount: Number(e.target.value) || 1 } as never)} className="w-full px-3 py-2.5 rounded-xl border-2 border-gray-200 bg-white" />
        </div>
        <label className="flex items-center gap-2 mt-7"><input type="checkbox" checked={f.hasBasement} onChange={(e) => sf({ ...f, hasBasement: e.target.checked } as never)} className="w-5 h-5 accent-navy" /><span className="text-sm font-bold">قبو</span></label>
        <label className="flex items-center gap-2 mt-7"><input type="checkbox" checked={f.hasRoofRoom} onChange={(e) => sf({ ...f, hasRoofRoom: e.target.checked } as never)} className="w-5 h-5 accent-navy" /><span className="text-sm font-bold">غرفة سطح</span></label>
      </div>

      <Nav onBack={onBack} onNext={onNext} canNext={!!f.buildingType} />
    </div>
  );
}

function FamilyStep({ form, setForm, onBack, onNext }: { form: never; setForm: never; onBack: () => void; onNext: () => void }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const f = form as any; const sf = setForm as never as (v: never) => void;
  const setMembers = (n: number) => {
    const list: Occupant[] = [...f.occupants].slice(0, n);
    while (list.length < n) list.push({ label: '', ageGroup: 'adult' });
    sf({ ...f, familyMembers: n, occupants: list } as never);
  };

  return (
    <div>
      <h2 className="text-2xl font-black text-navy-dark mb-2">👨‍👩‍👧‍👦 الأسرة والاحتياجات</h2>
      <p className="text-gray-600 mb-6 text-sm">احتياجاتك — يمكنك كتابة أسماء حقيقية للخصوصية، أو اختيار وصف عام (ابن، بنت، ماستر، خادمة).</p>

      <div className="mb-4">
        <Input label="عدد أفراد الأسرة" type="number" value={String(f.familyMembers)} onChange={(e: never) => setMembers(Number((e as never as { target: { value: string } }).target.value) || 0)} />
      </div>

      <fieldset className="border border-gray-200 rounded-xl p-4 mb-4">
        <legend className="px-2 text-sm font-bold text-navy-dark">🛏️ الغرف المطلوبة</legend>
        <p className="text-xs text-gray-500 mb-3">اكتب الاسم أو الوصف لكل غرفة:</p>
        <div className="space-y-2">
          {f.occupants.map((o: Occupant, i: number) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <Input value={o.label} onChange={(e: never) => { const arr = [...f.occupants]; arr[i] = { ...arr[i], label: (e as never as { target: { value: string } }).target.value }; sf({ ...f, occupants: arr } as never); }} placeholder="مثلاً: علي / غرفة ضيوف / ماستر" />
              <select value={o.ageGroup ?? 'adult'} onChange={(e) => { const arr = [...f.occupants]; arr[i] = { ...arr[i], ageGroup: e.target.value as never }; sf({ ...f, occupants: arr } as never); }} className="col-span-3 px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm">
                <option value="adult">بالغ</option><option value="kid">طفل</option>
              </select>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">💡 اقتراحات سريعة: <button onClick={() => { const arr = [...f.occupants]; arr.push({ label: 'ماستر', ageGroup: 'adult' }); sf({ ...f, occupants: arr, familyMembers: arr.length } as never); }} className="text-navy hover:underline mx-1">+ ماستر</button> · <button onClick={() => { const arr = [...f.occupants]; arr.push({ label: 'خادمة', ageGroup: 'adult' }); sf({ ...f, occupants: arr, familyMembers: arr.length } as never); }} className="text-navy hover:underline">+ خادمة</button></p>
      </fieldset>

      <Nav onBack={onBack} onNext={onNext} canNext />
    </div>
  );
}

function PreferencesStep({ form, setForm, isCommercial, onBack, onNext, loading }: { form: never; setForm: never; isCommercial: boolean; onBack: () => void; onNext: () => void; loading: boolean }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const f = form as any; const sf = setForm as never as (v: never) => void;
  return (
    <div>
      <h2 className="text-2xl font-black text-navy-dark mb-2">✨ تفضيلاتك</h2>
      <p className="text-gray-600 mb-6 text-sm">إعدادات إضافية — اختر ما يناسبك.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!isCommercial && (
          <>
            <ToggleCard icon="🛋️" label="مجلس رجال" on={f.preferences.hasMajlis} onToggle={(v) => sf({ ...f, preferences: { ...f.preferences, hasMajlis: v } } as never)} />
            <ToggleCard icon="💃" label="مجلس نساء منفصل" on={f.preferences.hasWomenMajlis} onToggle={(v) => sf({ ...f, preferences: { ...f.preferences, hasWomenMajlis: v } } as never)} />
            <ToggleCard icon="🛏️" label="غرفة خادمة" on={f.preferences.hasMaidRoom} onToggle={(v) => sf({ ...f, preferences: { ...f.preferences, hasMaidRoom: v } } as never)} />
            <ToggleCard icon="🌳" label="حديقة" on={f.preferences.hasGarden} onToggle={(v) => sf({ ...f, preferences: { ...f.preferences, hasGarden: v } } as never)} />
            <ToggleCard icon="💧" label="مسبح" on={f.preferences.hasPool} onToggle={(v) => sf({ ...f, preferences: { ...f.preferences, hasPool: v } } as never)} />
          </>
        )}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><span className="text-2xl">🚗</span><span className="font-bold">عدد المواقف</span></div>
          <input type="number" min={0} max={20} value={f.preferences.parkingSpots} onChange={(e) => sf({ ...f, preferences: { ...f.preferences, parkingSpots: Number(e.target.value) || 0 } } as never)} className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 bg-white" />
        </div>
      </div>

      <Nav onBack={onBack} onNext={onNext} canNext loading={loading} nextLabel="🚀 توليد التصميم" />
    </div>
  );
}

function ToggleCard({ icon, label, on, onToggle }: { icon: string; label: string; on: boolean; onToggle: (v: boolean) => void }) {
  return (
    <button onClick={() => onToggle(!on)} className={`p-4 rounded-xl border-2 text-right transition flex items-center gap-3 ${on ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
      <span className="text-2xl">{icon}</span>
      <span className="font-bold flex-1">{label}</span>
      <span className={`w-10 h-6 rounded-full relative transition ${on ? 'bg-green-500' : 'bg-gray-300'}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition ${on ? 'right-0.5' : 'right-4'}`} />
      </span>
    </button>
  );
}

function GeneratingStep({ error, onRetry: _onRetry }: { error?: string; onRetry: () => void }) {
  // 🚧 المنصة قيد التطوير — رسالة موحّدة
  if (error === 'UNDER_DEVELOPMENT' || error) {
    return (
      <div className="text-center py-12">
        <div className="text-7xl mb-5">🚧</div>
        <h2 className="text-3xl font-black text-amber-700 mb-3">قريباً جداً!</h2>
        <p className="text-gray-700 max-w-xl mx-auto mb-6 leading-relaxed">
          توليد التصميم الفعلي قيد التطوير حالياً. <br />
          نعمل على تحسين المحرك الهندسي ليُنتج مخططات احترافية بمعايير عالية.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-xl mx-auto mb-6 text-sm text-amber-800">
          ✨ <b>عند الإطلاق ستحصل على:</b>
          <ul className="text-right mt-2 space-y-1">
            <li>• تخطيط معماري حقيقي بجدران وأبواب ونوافذ</li>
            <li>• فحص امتثال للبلديات السعودية</li>
            <li>• تقدير تكلفة دقيق</li>
            <li>• ربط فوري مع مقاولي شركة صورة</li>
          </ul>
        </div>
        <Link to="/"><Button variant="primary">← العودة للموقع</Button></Link>
      </div>
    );
  }
  return (
    <div className="text-center py-16">
      <div className="text-6xl mb-4 animate-pulse">🤖</div>
      <h2 className="text-2xl font-black text-navy-dark mb-2">جاري توليد التصميم…</h2>
      <p className="text-gray-500">المحرك الذكي يحلّل أرضك وينشئ التخطيط الأمثل لك</p>
    </div>
  );
}

interface GenerateResult {
  rooms: Array<{ type: string; label: string; area: number; floor: number }>;
  layoutSvgs: string[];
  totalBuiltArea: number;
  outdoorArea: number;
  recommendations: string[];
  warnings: string[];
  hasPoolPossible: boolean;
  hasGardenPossible: boolean;
  compliance: { score: number; issues: string[]; suggestions: string[]; regulation: { city: string; maxFloors: number | null } | null };
}

function ResultStep({ projectId, result, onBack, navigate }: { projectId: string; result?: GenerateResult; onBack: () => void; navigate: (to: string) => void }) {
  const [floor, setFloor] = useState(0);
  const [showCost, setShowCost] = useState(false);
  const costM = useMutation({
    mutationFn: (quality: 'economic' | 'standard' | 'premium') => api.post<ApiSuccess<{ total: number; perSqm: number; foundation: number; structure: number; walls: number; roof: number; finishing: number; mep: number; exterior: number }>>(`/api/engineering/projects/${projectId}/cost-estimate`, { quality }),
  });
  const contractorM = useMutation({
    mutationFn: () => api.post(`/api/engineering/projects/${projectId}/request-contractor`, {}),
  });

  if (!result) return <div className="text-gray-500 text-center py-12">⟳ جاري التحميل…</div>;

  return (
    <div>
      <div className="text-center mb-6">
        <div className="text-5xl mb-2">✨</div>
        <h2 className="text-2xl font-black text-navy-dark">تصميمك جاهز!</h2>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPI title="مساحة البناء" value={`${result.totalBuiltArea.toFixed(0)} م²`} color="navy" />
        <KPI title="المساحة الخارجية" value={`${result.outdoorArea.toFixed(0)} م²`} color="green" />
        <KPI title="عدد الغرف" value={String(result.rooms.length)} color="amber" />
        <KPI title="نقاط الامتثال" value={`${result.compliance.score}/100`} color={result.compliance.score >= 70 ? 'green' : 'red'} />
      </div>

      {/* SVG */}
      <div className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden mb-4">
        <div className="bg-gray-50 border-b px-4 py-2 flex gap-2 flex-wrap">
          {result.layoutSvgs.map((_, i) => (
            <button key={i} onClick={() => setFloor(i)} className={`px-3 py-1 rounded-lg text-sm font-bold transition ${floor === i ? 'bg-navy-dark text-white' : 'bg-white border border-gray-200'}`}>
              {i === 0 ? 'الأرضي' : `دور ${i}`}
            </button>
          ))}
        </div>
        <div className="p-4 overflow-x-auto" dangerouslySetInnerHTML={{ __html: result.layoutSvgs[floor] ?? '' }} />
      </div>

      {/* Insights */}
      {result.recommendations.length > 0 && (
        <Card className="mb-3 bg-blue-50 border-blue-200">
          <CardBody>
            <h3 className="font-black mb-2 text-blue-900">💡 اقتراحات هندسية</h3>
            <ul className="space-y-1 text-sm text-blue-900">
              {result.recommendations.map((r, i) => <li key={i}>• {r}</li>)}
            </ul>
          </CardBody>
        </Card>
      )}

      {result.warnings.length > 0 && (
        <Card className="mb-3 bg-amber-50 border-amber-200">
          <CardBody>
            <h3 className="font-black mb-2 text-amber-900">⚠️ تحذيرات</h3>
            <ul className="space-y-1 text-sm text-amber-900">
              {result.warnings.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </CardBody>
        </Card>
      )}

      {/* امتثال */}
      <Card className={`mb-3 ${result.compliance.score >= 70 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <CardBody>
          <h3 className="font-black mb-2 flex items-center gap-2">🏛️ امتثال البلدية {result.compliance.regulation ? `(${result.compliance.regulation.city})` : '— لا توجد قواعد مسجّلة'}</h3>
          {result.compliance.issues.map((i, k) => <div key={k} className="text-sm text-red-700">• {i}</div>)}
          {result.compliance.suggestions.map((s, k) => <div key={k} className="text-sm text-blue-700">• {s}</div>)}
        </CardBody>
      </Card>

      {/* أزرار العمل */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <Button variant="primary" onClick={() => { setShowCost(true); costM.mutate('standard'); }} loading={costM.isPending}>💰 احسب التكلفة</Button>
        <Button variant="primary" onClick={() => contractorM.mutate()} loading={contractorM.isPending}>📞 اطلب مقاول من صورة</Button>
        <Button variant="outline" onClick={() => navigate(`/dashboard/engineering/${projectId}`)}>📂 حفظ في حسابي</Button>
      </div>

      {showCost && costM.data && (
        <Card className="mb-3">
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">💰 تقدير تكلفة البناء (جودة قياسية)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              {(['economic', 'standard', 'premium'] as const).map((q) => (
                <Button key={q} size="sm" variant="ghost" onClick={() => costM.mutate(q)}>{q === 'economic' ? 'اقتصادي' : q === 'standard' ? 'قياسي' : 'فاخر'}</Button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <CostItem label="الأساسات" value={costM.data.data.foundation} />
              <CostItem label="الهيكل" value={costM.data.data.structure} />
              <CostItem label="الجدران" value={costM.data.data.walls} />
              <CostItem label="السقف" value={costM.data.data.roof} />
              <CostItem label="التشطيبات" value={costM.data.data.finishing} />
              <CostItem label="الميكانيك والكهرباء" value={costM.data.data.mep} />
              <CostItem label="الأعمال الخارجية" value={costM.data.data.exterior} />
            </div>
            <div className="bg-navy-dark text-white rounded-xl p-4 mt-3 flex justify-between items-center">
              <span className="font-black">الإجمالي التقديري</span>
              <span className="text-2xl font-black text-gold">{costM.data.data.total.toLocaleString('en-US')} ر.س</span>
            </div>
            <div className="text-xs text-gray-500 mt-2 text-center">≈ {costM.data.data.perSqm.toFixed(0)} ر.س / م² · للاستئناس فقط — الأسعار الفعلية تختلف</div>
          </CardBody>
        </Card>
      )}

      {contractorM.data && (
        <Card className="bg-green-50 border-green-200">
          <CardBody><div className="text-center text-green-800"><div className="text-3xl mb-2">✓</div><div className="font-black">تم استلام طلبك! سيتواصل معك فريق المقاولين من شركة صورة قريباً.</div></div></CardBody>
        </Card>
      )}

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="ghost" onClick={onBack}>← تصميم جديد</Button>
      </div>
    </div>
  );
}

function CostItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-gray-50 rounded-lg p-2 flex justify-between"><span>{label}</span><b>{value.toLocaleString('en-US')}</b></div>
  );
}

function KPI({ title, value, color }: { title: string; value: string; color: 'navy' | 'green' | 'amber' | 'red' }) {
  const colors = { navy: 'text-navy-dark', green: 'text-green-600', amber: 'text-amber-600', red: 'text-red-600' };
  return (
    <Card><CardBody>
      <div className="text-xs text-gray-500 mb-1">{title}</div>
      <div className={`text-xl font-black ${colors[color]}`}>{value}</div>
    </CardBody></Card>
  );
}

function Nav({ onBack, onNext, canNext, loading, nextLabel }: { onBack: () => void; onNext: () => void; canNext: boolean; loading?: boolean; nextLabel?: string }) {
  return (
    <div className="flex justify-between gap-2 mt-6 pt-6 border-t">
      <Button variant="ghost" onClick={onBack}>← السابق</Button>
      <Button variant="primary" onClick={onNext} disabled={!canNext} loading={loading}>{nextLabel ?? 'التالي →'}</Button>
    </div>
  );
}
